import { useEffect, useRef } from 'react';

const KEYS: readonly { readonly digit: string; readonly letters: string }[] = [
    { digit: '1', letters: '' },
    { digit: '2', letters: 'ABC' },
    { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' },
    { digit: '5', letters: 'JKL' },
    { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' },
    { digit: '8', letters: 'TUV' },
    { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' },
    { digit: '0', letters: '' },
    { digit: '#', letters: '' },
];

/** Hold duration on the `0` key before it types `+` instead of a digit. */
const HOLD_FOR_PLUS_MS = 2000;

interface DialKeypadProps {
    readonly onPress: (digit: string) => void;
    /**
     * Long-press action for the `0` key (phone-dialer convention: hold zero to
     * get the `+` E.164 prefix). Left unset by the in-call keypad — `+` is not
     * a DTMF tone, so holding zero there must stay a plain `0`.
     */
    readonly onHoldZero?: () => void;
    /** Every key inert — the idle dialer when there is no number to call from. */
    readonly disabled?: boolean;
}

/**
 * 3x4 DTMF grid of circular, liquid-glass keys (iOS-dialer style) — used both
 * in the idle dialer and as an in-call overlay. `auto-rows-fr` +
 * `place-items-center` keep every key a centered circle; when the widget is
 * resized taller the rows share the extra space as breathing room, and wider
 * columns grow the circles up to their cap.
 */
export function DialKeypad({ onPress, onHoldZero, disabled = false }: DialKeypadProps) {
    const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const didHold = useRef(false);

    const cancelHold = (): void => {
        if (holdTimer.current) clearTimeout(holdTimer.current);
        holdTimer.current = null;
    };

    const startHold = (): void => {
        didHold.current = false;
        holdTimer.current = setTimeout(() => {
            didHold.current = true;
            onHoldZero?.();
        }, HOLD_FOR_PLUS_MS);
    };

    useEffect(() => cancelHold, []);

    const handleClick = (digit: string): void => {
        // The hold already produced a '+', so releasing must not ALSO type a
        // '0' — `click` fires after `pointerup` on every pointer device.
        if (didHold.current) {
            didHold.current = false;
            return;
        }
        onPress(digit);
    };

    return (
        <div className="grid flex-1 auto-rows-fr grid-cols-3 place-items-center gap-1.5">
            {KEYS.map(({ digit, letters }) => {
                const canHold = digit === '0' && onHoldZero !== undefined;
                return (
                    <button
                        key={digit}
                        type="button"
                        disabled={disabled}
                        title={canHold ? 'Hold for +' : undefined}
                        onClick={() => handleClick(digit)}
                        {...(canHold
                            ? {
                                  onPointerDown: startHold,
                                  onPointerUp: cancelHold,
                                  onPointerCancel: cancelHold,
                                  onPointerLeave: cancelHold,
                                  // A long press on touch otherwise raises the
                                  // native context menu mid-hold.
                                  onContextMenu: (event: React.MouseEvent) =>
                                      event.preventDefault(),
                              }
                            : {})}
                        className="flex aspect-square w-[60%] max-w-[calc(4.75rem*var(--voice-scale,1))] min-w-12 flex-col items-center justify-center rounded-full border border-white/60 bg-gradient-to-b from-white/80 to-white/45 text-text-primary shadow-[0_2px_8px_-2px_rgb(15_23_42/0.18),inset_0_1px_0_0_rgb(255_255_255/0.85),inset_0_-1px_2px_0_rgb(15_23_42/0.05)] backdrop-blur-sm backdrop-saturate-150 transition-all select-none disabled:pointer-events-none disabled:opacity-40 hover:from-white/95 hover:to-white/65 focus-visible:ring-2 focus-visible:ring-primary-500/50 focus-visible:outline-none active:from-white active:to-white/80 motion-safe:active:scale-[0.93] dark:border-white/[0.12] dark:from-white/[0.14] dark:to-white/[0.05] dark:shadow-[0_2px_8px_-2px_rgb(0_0_0/0.55),inset_0_1px_0_0_rgb(255_255_255/0.14)] dark:hover:from-white/[0.2] dark:hover:to-white/[0.09] dark:active:from-white/[0.26] dark:active:to-white/[0.12]"
                    >
                        <span className="text-[calc(1.125rem*var(--voice-scale,1))] leading-none font-medium tabular-nums">
                            {digit}
                        </span>
                        {/* Fixed-height sub-label keeps digits aligned across rows.
                            On '0' it advertises the hold-for-'+' gesture, which is
                            otherwise invisible. */}
                        <span
                            className={
                                canHold
                                    ? 'mt-0.5 h-[calc(0.5rem*var(--voice-scale,1))] text-[calc(10px*var(--voice-scale,1))] leading-none font-semibold text-text-tertiary'
                                    : 'mt-0.5 h-[calc(0.5rem*var(--voice-scale,1))] text-[calc(8px*var(--voice-scale,1))] leading-none font-medium tracking-[0.18em] text-text-tertiary uppercase'
                            }
                        >
                            {canHold ? '+' : letters}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
